import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import { ArrowLeft, Gamepad2, ScrollText, Trophy, Sparkles } from "lucide-react";

function LeagueCard({ league }) {
  return (
    <Link
      to={`/leagues/${league.id}`}
      className="group block bg-[#111113]/90 border border-white/10 rounded-2xl overflow-hidden hover:border-gold-500/40 transition-all duration-300 hover:-translate-y-1 shadow-2xl relative flex flex-col justify-between"
      data-testid={`league-card-${league.id}`}
    >
      {/* 🖼️ صورة غلاف الدوري أو الأيقونة الافتراضية */}
      <div className="relative h-44 w-full overflow-hidden bg-black/40 border-b border-white/5">
        {league.rules_image ? (
          <img
            src={league.rules_image}
            alt={league.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            data-testid={`league-image-${league.id}`}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-gold-500/10 via-black/40 to-transparent grid place-items-center">
            <Trophy size={54} className="text-gold-500/30 group-hover:scale-110 transition-transform duration-300" />
          </div>
        )}

        {/* وسام حالة الدوري (نشط / سوبر رايفلز) */}
        <div className="absolute top-3 right-3 flex items-center gap-2 flex-wrap">
          {league.status === "active" ? (
            <span className="text-[10px] font-black uppercase tracking-widest text-emerald-400 bg-emerald-500/20 border border-emerald-500/40 rounded-lg px-2.5 py-1 backdrop-blur-md shadow-sm flex items-center gap-1">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> نشط الآن
            </span>
          ) : (
            <span className="text-[10px] font-black uppercase tracking-widest text-gold-400 bg-black/60 border border-gold-500/30 rounded-lg px-2.5 py-1 backdrop-blur-md">
              مكتمل 🏆
            </span>
          )}

          {league.super_rivals_enabled && (
            <span className="text-[10px] font-black uppercase tracking-widest text-gold-300 bg-gold-500/20 border border-gold-500/40 rounded-lg px-2.5 py-1 backdrop-blur-md flex items-center gap-1">
              <Sparkles size={11} className="text-gold-400 shrink-0" /> سوبر رايفلز
            </span>
          )}
        </div>
      </div>

      {/* 📝 تفاصيل الدوري */}
      <div className="p-5 space-y-4 flex-1 flex flex-col justify-between">
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <h2 className="font-display font-black text-xl text-white group-hover:text-gold-400 transition-colors truncate">
              {league.name}
            </h2>
          </div>

          <div className="flex items-center gap-2 text-xs font-bold text-white/50">
            <Gamepad2 size={14} className="text-gold-500 shrink-0" /> {league.game || "Call of Duty"}
          </div>

          {league.description ? (
            <p className="text-xs text-white/60 leading-relaxed line-clamp-2 pt-1">
              {league.description}
            </p>
          ) : (
            <p className="text-xs text-white/30 italic pt-1">— لا يوجد وصف مختصر —</p>
          )}
        </div>

        {/* 🔗 الشريط السفلي */}
        <div className="pt-3 border-t border-white/5 flex items-center justify-between text-xs text-white/45">
          <span className="inline-flex items-center gap-1.5 text-white/50 font-medium">
            <ScrollText size={13} className="text-white/30" /> قوانين مخصصة
          </span>
          <span className="inline-flex items-center gap-1 text-gold-500 font-bold group-hover:text-gold-400 transition-transform group-hover:-translate-x-1">
            عرض جدول الترتيب والتفاصيل <ArrowLeft size={13} />
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function LeaguesPage() {
  const [leagues, setLeagues] = useState(null);

  useEffect(() => {
    api
      .get("/leagues/active")
      .then((r) => setLeagues(r.data || []))
      .catch(() => setLeagues([]));
  }, []);

  return (
    <div className="space-y-8" dir="rtl">
      {/* ── Header ── */}
      <div>
        <div className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full bg-gold-500/10 border border-gold-500/20 text-gold-500 text-xs font-bold mb-2">
          <Trophy size={14} /> منافسات الدوريات
        </div>
        <h1 className="font-display font-black text-3xl md:text-5xl text-white">
          الدوريات النشطة
        </h1>
        <p className="text-white/50 mt-1.5 text-sm">
          لكل دوري جدول ترتيب ومسابقة مستقلة — الفوز (+3 نقاط) • الخسارة (-1) • الانسحاب (-3 نقاط)
        </p>
      </div>

      {/* ── Loading State ── */}
      {leagues === null && (
        <div className="text-white/40 text-xs py-12 text-center bg-[#111113]/40 rounded-2xl border border-white/5">
          جاري تحميل الدوريات المتاحة...
        </div>
      )}

      {/* ── Empty State ── */}
      {leagues && leagues.length === 0 && (
        <div
          data-testid="leagues-empty-state"
          className="bg-[#111113]/90 border border-white/10 rounded-2xl p-12 text-center text-white/50 space-y-2"
        >
          <Trophy size={36} className="mx-auto text-white/20 mb-3" />
          <div className="font-bold text-base text-white">لا توجد دوريات نشطة حالياً</div>
          <p className="text-xs text-white/40">ترقّب الإعلان عن مواسم الدوريات القادمة قريباً!</p>
        </div>
      )}

      {/* ── Leagues Grid ── */}
      {leagues && leagues.length > 0 && (
        <div className="grid md:grid-cols-2 gap-6" data-testid="leagues-grid">
          {leagues.map((lg) => (
            <LeagueCard key={lg.id} league={lg} />
          ))}
        </div>
      )}
    </div>
  );
}
